import {
  Controller,
  FieldValues,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  RadioButton,
  RadioGroup,
  Stack,
  UseFormRegister,
  useFormContext,
} from '@agrotoken-tech/ui';
import { NoInfer } from '../../utils/typescript.utils';

interface Props<T> {
  name: string;
  label: string;
  options: Option<T>[];
  defaultValue: NoInfer<T>;
  rhfOptions?: Parameters<UseFormRegister<FieldValues>>[1];
  errorMessageFormatter?(message: string, ...rest: any[]): string;
  helperText?: string;
}

interface Option<T> {
  label: string;
  value: T;
}

// TODO: Move this component to agrotoken UI
export const RadioField = <T extends string>({
  name,
  label,
  options = [],
  defaultValue,
  rhfOptions = {},
  errorMessageFormatter = (message: string): string => message,
  helperText,
}: Props<T>) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const fieldError = errors[name];
  const errorMessage = errorMessageFormatter(fieldError?.message as string);
  const isInvalid = Boolean(fieldError);
  const { disabled, ...restRhfOptions } = rhfOptions;

  const showHelperText = !isInvalid && helperText;

  return (
    <FormControl
      isInvalid={isInvalid}
      isRequired={Boolean(rhfOptions.required)}
    >
      <Flex direction="column">
        <FormLabel
          variant="medium"
          size="sm"
          color="content-secondary"
          marginBottom={1.5}
          display={label ? 'block' : 'none'}
          htmlFor={name}
        >
          {label}
        </FormLabel>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <RadioGroup {...field}>
              <Stack spacing={4} direction="row">
                {options.map(({ label, value }, index) => (
                  <RadioButton
                    key={index}
                    size="md"
                    value={value}
                    label={label}
                    disabled={disabled}
                  />
                ))}
              </Stack>
            </RadioGroup>
          )}
          defaultValue={defaultValue}
          rules={restRhfOptions}
        />
      </Flex>
      {showHelperText && <FormHelperText>{helperText}</FormHelperText>}
      <FormErrorMessage>{errorMessage}</FormErrorMessage>
    </FormControl>
  );
};
